import { useState } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard, useWriteForbiddenGuard } from '../api/guard'
import { writeErrorMessage } from '../api/problems'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { CreditsPanel, OverridesPanel } from '../components/WorkspaceEntitlements'
import './screens.css'

interface Workspace {
  id: string
  name: string
  execution_mode: string
  created_at: string
  usage_count: number
  // Current plan name; null when the workspace has none.
  plan: string | null
}
interface WorkspacesResponse {
  workspaces: Workspace[]
  total: number
}

interface PlanAssignResult {
  workspace_id: string
  plan: string
  previous_plan: string | null
  changed: boolean
}

// GET /admin/plans: name + limits only (no prices until billing exists, D3).
interface Plan {
  name: string
  quota_monthly: number
  rate_limit_per_minute: number
  max_members: number | null
  max_workspaces: number | null
  request_tracing_enabled: boolean
  webhooks_config_enabled: boolean
  log_retention_days: number
  deploy_community_only: boolean
}
interface PlansResponse {
  plans: Plan[]
  total: number
}

const PLANS_QUERY_KEY = ['admin-plans'] as const

const PAGE_SIZE = 20

const unlimited = (n: number | null) => (n === null ? 'unlimited' : n.toLocaleString())

function planLimits(p: Plan): string {
  return [
    `${p.quota_monthly.toLocaleString()} calls/month`,
    `${p.rate_limit_per_minute.toLocaleString()}/min`,
    `members: ${unlimited(p.max_members)}`,
    `workspaces: ${unlimited(p.max_workspaces)}`,
    `${p.log_retention_days}-day log retention`,
  ].join(' · ')
}

interface WorkspacesViewProps {
  // The sidebar's Plans entry renders this same list (plan assignment lives per workspace).
  title?: string
}

export function WorkspacesView({ title = 'Workspaces' }: WorkspacesViewProps) {
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [planFor, setPlanFor] = useState<Workspace | null>(null)
  const [planName, setPlanName] = useState('')
  const [planMsg, setPlanMsg] = useState<string | null>(null)
  // Workspace detail: entitlement overrides + credits (entitlements E4).
  const [detailFor, setDetailFor] = useState<Workspace | null>(null)
  const qc = useQueryClient()

  // Only fetched once the dialog opens; cached afterwards.
  const plans = useQuery<PlansResponse>({
    queryKey: PLANS_QUERY_KEY,
    queryFn: () => apiFetch<PlansResponse>('/admin/plans'),
    enabled: planFor !== null,
  })
  useForbiddenGuard(plans.error)
  const planList = plans.data?.plans ?? []
  const selectedPlan = planList.find((p) => p.name === planName)

  const assign = useMutation({
    mutationFn: (vars: { workspace: Workspace; plan: string }) =>
      apiFetch<PlanAssignResult>(
        `/admin/workspaces/${encodeURIComponent(vars.workspace.id)}/plan`,
        { method: 'PATCH', body: JSON.stringify({ plan: vars.plan }) },
      ),
    onSuccess: (data, vars) => {
      // Update the row in place from the PATCH response (every cached page/search).
      qc.setQueriesData<WorkspacesResponse>({ queryKey: ['admin-workspaces'] }, (old) =>
        old
          ? {
              ...old,
              workspaces: old.workspaces.map((w) =>
                w.id === data.workspace_id ? { ...w, plan: data.plan } : w,
              ),
            }
          : old,
      )
      setPlanMsg(
        data.changed
          ? `${vars.workspace.name}: plan changed from ${data.previous_plan ?? '—'} to ${data.plan}.`
          : `${vars.workspace.name} is already on ${data.plan}; nothing changed.`,
      )
    },
    onError: () => setPlanMsg(null),
  })
  useWriteForbiddenGuard(assign.error)

  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) })
  if (search) params.set('search', search)

  const query = useQuery<WorkspacesResponse>({
    queryKey: ['admin-workspaces', search, offset],
    queryFn: () => apiFetch<WorkspacesResponse>(`/admin/workspaces?${params.toString()}`),
    placeholderData: keepPreviousData,
  })
  useForbiddenGuard(query.error)

  const total = query.data?.total ?? 0
  const rows = query.data?.workspaces ?? []
  const hasPrev = offset > 0
  const hasNext = offset + PAGE_SIZE < total

  return (
    <section className="ws" aria-labelledby="ws-heading">
      <div className="ws-head">
        <h2 id="ws-heading">{title}</h2>
        <input
          className="ws-search"
          type="search"
          aria-label="Search workspaces"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setOffset(0)
          }}
        />
      </div>

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="ws-error" role="alert">
          Failed to load workspaces.
        </p>
      )}

      {assign.isError && (
        <p className="ws-error" role="alert">
          {writeErrorMessage(assign.error)}
        </p>
      )}
      {planMsg && <p className="scr-ok">{planMsg}</p>}

      <table className="ws-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Mode</th>
            <th>Created</th>
            <th>Usage</th>
            <th>Current plan</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => (
            <tr key={w.id}>
              <td>{w.name}</td>
              <td>{w.execution_mode}</td>
              <td>{new Date(w.created_at).toLocaleDateString()}</td>
              <td>{w.usage_count}</td>
              <td>{w.plan ?? '—'}</td>
              <td>
                <button
                  type="button"
                  className="screen-btn"
                  aria-label={`Assign plan to ${w.name}`}
                  disabled={assign.isPending}
                  onClick={() => {
                    setPlanName(w.plan ?? '')
                    setPlanFor(w)
                  }}
                >
                  Assign plan
                </button>{' '}
                <button
                  type="button"
                  className="screen-btn"
                  aria-label={`Overrides and credits for ${w.name}`}
                  aria-pressed={detailFor?.id === w.id}
                  onClick={() => setDetailFor(detailFor?.id === w.id ? null : w)}
                >
                  Overrides &amp; credits
                </button>
              </td>
            </tr>
          ))}
          {!query.isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={6} className="ws-empty">
                No workspaces.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="ws-pager">
        <button
          type="button"
          disabled={!hasPrev}
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
        >
          Previous
        </button>
        <span className="ws-count">
          {total === 0 ? '0' : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)}`} of {total}
        </span>
        <button type="button" disabled={!hasNext} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
          Next
        </button>
      </div>

      {detailFor && (
        <section className="scr" aria-label={`Workspace detail: ${detailFor.name}`}>
          <div className="screen-head">
            <h3>
              {detailFor.name} <span className="scr-muted scr-mono">{detailFor.id}</span>
            </h3>
            <button type="button" className="screen-btn" onClick={() => setDetailFor(null)}>
              Close
            </button>
          </div>
          <p className="scr-note">
            Plan: {rows.find((w) => w.id === detailFor.id)?.plan ?? detailFor.plan ?? '—'}
          </p>
          <OverridesPanel key={`o-${detailFor.id}`} workspace={detailFor} />
          <CreditsPanel key={`c-${detailFor.id}`} workspace={detailFor} />
        </section>
      )}

      {planFor && (
        <ConfirmDialog
          label="Confirm plan assignment"
          confirmText="Assign"
          confirmDisabled={!selectedPlan}
          onCancel={() => setPlanFor(null)}
          onConfirm={() => {
            setPlanMsg(null)
            assign.mutate({ workspace: planFor, plan: planName })
            setPlanFor(null)
          }}
        >
          <p>
            Assign a plan to <strong>{planFor.name}</strong>. This changes the workspace&apos;s
            entitlements immediately.
          </p>
          <p className="scr-note">Current plan: {planFor.plan ?? '—'}</p>
          {plans.isLoading && <p className="scr-note">Loading plans…</p>}
          {plans.isError && (plans.error as { status?: number }).status !== 403 && (
            <p className="scr-err" role="alert">
              Failed to load plans.
            </p>
          )}
          {plans.isSuccess && planList.length === 0 && (
            <p className="scr-note">No plans are defined.</p>
          )}
          {planList.length > 0 && (
            <select
              className="scr-input"
              aria-label="Plan"
              aria-describedby="plan-limits"
              value={selectedPlan ? planName : ''}
              onChange={(e) => setPlanName(e.target.value)}
            >
              <option value="" disabled>
                Choose a plan…
              </option>
              {planList.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          <p id="plan-limits" className="scr-note">
            {selectedPlan ? planLimits(selectedPlan) : 'Pick a plan to see its limits.'}
          </p>
        </ConfirmDialog>
      )}
    </section>
  )
}
